// @spec SHL-STR-050
// API client functions for organization settings with branding

import type { ApiClient } from '@/lib/api-client';

/**
 * Address for organization settings.
 */
export interface Address {
  street: string;
  city: string;
  state: string | null;
  postalCode: string;
  country: string;
}

/**
 * Security settings for organization.
 */
export interface SecuritySettings {
  requireMfa: boolean;
  mfaGracePeriodDays: number;
  sessionTimeoutMinutes: number;
  maxSessionDurationHours: number;
  allowedAuthMethods: string[];
  requireReauthForSensitiveActions: boolean;
}

/**
 * Data visibility settings for organization.
 */
export interface DataVisibilitySettings {
  parentCanViewData: boolean;
  shareableDataTypes: string[];
  aggregatedReportingEnabled: boolean;
}

/**
 * Parent organization information.
 */
export interface ParentOrganizationInfo {
  id: string;
  name: string;
  slug: string;
}

/**
 * Branding settings for Enterprise organizations.
 */
export interface BrandingSettings {
  primaryColor: string | null;
  faviconUrl: string | null;
  emailHeaderLogoUrl: string | null;
}

/**
 * Full organization settings response.
 */
export interface OrganizationSettingsResponse {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  logoUrl: string | null;

  // Contact info
  contactEmail: string | null;
  contactPhone: string | null;
  address: Address | null;

  // Business context
  industry: string | null;
  employeeCount: string | null;

  // Hierarchy
  parentOrganization: ParentOrganizationInfo | null;
  level: number;

  // Security settings
  securitySettings: SecuritySettings;

  // Data visibility
  dataVisibilitySettings: DataVisibilitySettings;

  // Branding (Enterprise)
  branding: BrandingSettings | null;

  // Status
  status: string;

  // Timestamps
  createdAt: string;
  updatedAt: string;

  // Archive fields
  archiveRequestedAt: string | null;
  archivedAt: string | null;
  archiveScheduledFor: string | null;
}

/**
 * Input for updating branding settings.
 */
export interface BrandingUpdateInput {
  primaryColor?: string | null;
  faviconUrl?: string | null;
  emailHeaderLogoUrl?: string | null;
}

/**
 * Input for updating organization settings.
 */
export interface OrganizationUpdateInput {
  name?: string;
  slug?: string;
  description?: string;
  logoUrl?: string;
  contactEmail?: string;
  contactPhone?: string;
  address?: Address;
  industry?: string;
  employeeCount?: string;
  securitySettings?: {
    sessionTimeoutMinutes?: number;
    maxSessionDurationHours?: number;
    requireReauthForSensitiveActions?: boolean;
  };
  branding?: BrandingUpdateInput;
}

/**
 * Fetch organization settings including branding information.
 *
 * @param apiClient - Configured API client with auth token
 * @param organizationId - The organization ID to fetch settings for
 * @returns Organization settings with branding data
 *
 * @example
 * const { getToken } = useAuth();
 * const client = createApiClient({ getAuthToken: getToken });
 * const settings = await fetchOrganizationSettings(client, 'org-123');
 */
export async function fetchOrganizationSettings(
  apiClient: ApiClient,
  organizationId: string
): Promise<OrganizationSettingsResponse> {
  return apiClient.get<OrganizationSettingsResponse>(`/organizations/${organizationId}/settings`);
}

/**
 * Update organization settings including branding (admin only).
 *
 * @param apiClient - Configured API client with auth token
 * @param organizationId - The organization ID to update
 * @param data - The fields to update
 * @returns Updated organization settings
 *
 * @example
 * const { getToken } = useAuth();
 * const client = createApiClient({ getAuthToken: getToken });
 * const updated = await updateOrganizationSettings(client, 'org-123', {
 *   branding: { primaryColor: '#2563EB' }
 * });
 */
export async function updateOrganizationSettings(
  apiClient: ApiClient,
  organizationId: string,
  data: OrganizationUpdateInput
): Promise<OrganizationSettingsResponse> {
  return apiClient.patch<OrganizationSettingsResponse>(
    `/organizations/${organizationId}/settings`,
    data
  );
}
